import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { commandUuid, messageApi, MessageApiError, normalizeError } from './api';
import {
  CommandRequest,
  CommandResult,
  CommandType,
  JsonValue,
  MessageDetail,
  MessagePage,
  MessageQuery,
  MessageStats,
  Review,
  ReviewFields
} from './types';
import { collectFieldIssues, compatibleReview, reasonIssue, seedingReview } from './reviewUiState';

export const emptyFields: ReviewFields = {
  title: '',
  summary: '',
  interpretation: '',
  importance: 'GENERAL',
  featured: false,
  reason: '',
  topic: '',
  keyFacts: [],
  shortTermImpact: '',
  mediumTermImpact: '',
  actionGuidance: '',
  eventReconstruction: '',
  confidenceAnalysis: '',
  transmissionPath: [],
  expectationGap: '',
  actionItems: []
};

type SeedSource = 'review' | 'analysis' | 'empty';

/** Only stored JSON string arrays become editable list items; other element types are dropped, not invented. */
function asStringArray(value: JsonValue | undefined): string[] {
  return Array.isArray(value) ? value.filter((item): item is string => typeof item === 'string') : [];
}
function sameArray(values: string[], stored: JsonValue | undefined): boolean {
  return Array.isArray(stored) && stored.length === values.length && stored.every((item, index) => item === values[index]);
}
/** Optional structured fields as stored on analysis or review rows. Missing values stay empty; they are never invented. */
type StructuredSeed = {
  topic?: string | null;
  keyFacts?: JsonValue;
  shortTermImpact?: string | null;
  mediumTermImpact?: string | null;
  actionGuidance?: string | null;
  eventReconstruction?: string | null;
  confidenceAnalysis?: string | null;
  transmissionPath?: JsonValue;
  expectationGap?: string | null;
  actionItems?: JsonValue;
};
function structuredFrom(stored: StructuredSeed): Pick<
  ReviewFields,
  | 'topic'
  | 'keyFacts'
  | 'shortTermImpact'
  | 'mediumTermImpact'
  | 'actionGuidance'
  | 'eventReconstruction'
  | 'confidenceAnalysis'
  | 'transmissionPath'
  | 'expectationGap'
  | 'actionItems'
> {
  return {
    topic: stored.topic ?? '',
    keyFacts: asStringArray(stored.keyFacts),
    shortTermImpact: stored.shortTermImpact ?? '',
    mediumTermImpact: stored.mediumTermImpact ?? '',
    actionGuidance: stored.actionGuidance ?? '',
    eventReconstruction: stored.eventReconstruction ?? '',
    confidenceAnalysis: stored.confidenceAnalysis ?? '',
    transmissionPath: asStringArray(stored.transmissionPath),
    expectationGap: stored.expectationGap ?? '',
    actionItems: asStringArray(stored.actionItems)
  };
}
function initialFields(detail: MessageDetail): { fields: ReviewFields; source: SeedSource } {
  const review = seedingReview(detail);
  if (review) {
    return {
      source: 'review',
      fields: {
        ...structuredFrom(review),
        title: review.title,
        summary: review.summary,
        interpretation: review.interpretation,
        importance: review.importance,
        featured: review.featured,
        reason: review.reason
      }
    };
  }
  const analysis = detail.analysis;
  if (analysis && analysis.articleSchemaVersion === '2') {
    return { source: 'analysis', fields: { ...structuredFrom(analysis), ...baseFrom(detail) } };
  }
  return { source: 'empty', fields: { ...emptyFields, ...baseFrom(detail) } };
}
function baseFrom(detail: MessageDetail): Pick<ReviewFields, 'title' | 'summary' | 'interpretation' | 'importance' | 'featured' | 'reason'> {
  return {
    title: detail.text?.title ?? '',
    summary: detail.analysis?.summary ?? '',
    interpretation: detail.analysis?.interpretation ?? '',
    importance: detail.analysis?.importance ?? 'GENERAL',
    featured: false,
    reason: ''
  };
}

function fieldsMatchReview(fields: ReviewFields, review: Review): boolean {
  return (
    fields.title === review.title &&
    fields.summary === review.summary &&
    fields.interpretation === review.interpretation &&
    fields.importance === review.importance &&
    fields.featured === review.featured &&
    fields.topic === (review.topic ?? '') &&
    sameArray(fields.keyFacts, review.keyFacts) &&
    fields.shortTermImpact === (review.shortTermImpact ?? '') &&
    fields.mediumTermImpact === (review.mediumTermImpact ?? '') &&
    fields.actionGuidance === (review.actionGuidance ?? '') &&
    fields.eventReconstruction === (review.eventReconstruction ?? '') &&
    fields.confidenceAnalysis === (review.confidenceAnalysis ?? '') &&
    sameArray(fields.transmissionPath, review.transmissionPath) &&
    fields.expectationGap === (review.expectationGap ?? '') &&
    sameArray(fields.actionItems, review.actionItems)
  );
}

function commandEffectNotice(type: CommandType, next: MessageDetail | null): string {
  if (!next) return '操作已完成，页面状态待刷新';
  if (type === 'PUBLISH' && next.publication?.visible) return '已发布';
  if (type === 'WITHDRAW' && next.manualBlock && !next.publication?.visible) return '已拦截';
  if (type === 'UNBLOCK' && !next.manualBlock && !next.publication?.visible) return '已解除拦截，尚未公开';
  return '操作已完成，页面状态待刷新';
}

function isMetricCount(value: unknown): value is { today: number; cumulative: number } {
  if (!value || typeof value !== 'object') return false;
  const count = value as { today?: unknown; cumulative?: unknown };
  return typeof count.today === 'number' && typeof count.cumulative === 'number';
}
function isMessageStats(value: unknown): value is MessageStats {
  if (!value || typeof value !== 'object') return false;
  const stats = value as MessageStats;
  return (
    isMetricCount(stats.messageTotal) &&
    isMetricCount(stats.normalization?.completed) &&
    isMetricCount(stats.analysis?.completed) &&
    isMetricCount(stats.analysis?.results?.validComplete) &&
    isMetricCount(stats.review?.required) &&
    isMetricCount(stats.publication?.unpublished)
  );
}

export function useMessageManagement() {
  const [query, setQuery] = useState<MessageQuery>({
    page: 1,
    size: 20,
    q: '',
    aiPhase: '',
    reviewState: '',
    publicationState: ''
  });
  const [page, setPage] = useState<MessagePage | null>(null);
  const [listLoading, setListLoading] = useState(true);
  const [listError, setListError] = useState<MessageApiError | null>(null);
  const [stats, setStats] = useState<MessageStats | null>(null);
  const [statsLoading, setStatsLoading] = useState(true);
  const [statsError, setStatsError] = useState<MessageApiError | null>(null);
  const [detail, setDetail] = useState<MessageDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState<MessageApiError | null>(null);
  const [fields, setFields] = useState<ReviewFields>(emptyFields);
  const [reviewBaselineId, setReviewBaselineId] = useState<string | null>(null);
  const [dirty, setDirty] = useState(false);
  const [writeBusyIds, setWriteBusyIds] = useState<Record<string, true>>({});
  const [actionError, setActionError] = useState<MessageApiError | null>(null);
  const [notice, setNotice] = useState('');
  const [receipts, setReceipts] = useState<Record<string, CommandResult>>({});
  const [retries, setRetries] = useState<Record<string, CommandRequest>>({});
  const [queryingIds, setQueryingIds] = useState<Record<string, true>>({});
  const [pollTimedOutIds, setPollTimedOutIds] = useState<Record<string, true>>({});
  const [conflictIds, setConflictIds] = useState<Record<string, true>>({});
  const [unblockResaveIds, setUnblockResaveIds] = useState<Record<string, true>>({});
  const [committedReasons, setCommittedReasons] = useState<Record<string, string>>({});
  const [lastSavedIds, setLastSavedIds] = useState<Record<string, string>>({});
  const [seedSource, setSeedSource] = useState<SeedSource>('empty');
  const [selectedId, setSelectedId] = useState('');
  const [drawerOpen, setDrawerOpen] = useState(false);
  const selected = useRef('');
  const detailInitialized = useRef(false);
  const currentQuery = useRef(query);
  currentQuery.current = query;
  const listSequence = useRef(0);
  const statsSequence = useRef(0);
  const detailSequence = useRef(0);
  const mounted = useRef(true);
  const pollInFlight = useRef<Record<string, boolean>>({});
  const pollStartedAt = useRef<Record<string, number>>({});
  // A PUBLISH targets the latest immutable review, not the values shown in this form.
  // Only a displayed review or this form's own save result may establish its baseline.
  // Operation reason is independent of the published content and can intentionally differ.
  // Every structured field participates: publishing an unseen or edited article field must be impossible.
  const reviewMismatch = Boolean(
    detail && (!detail.review || reviewBaselineId !== detail.review.id || !fieldsMatchReview(fields, detail.review))
  );
  const articleDirty = dirty;
  const reasonDirty = Boolean(selectedId) && fields.reason !== (committedReasons[selectedId] ?? '');
  const writeBusy = Boolean(selectedId && writeBusyIds[selectedId]);
  const querying = Boolean(selectedId && queryingIds[selectedId]);
  const pollTimedOut = Boolean(selectedId && pollTimedOutIds[selectedId]);
  const conflictPending = Boolean(selectedId && conflictIds[selectedId]);
  const requireSaveAfterUnblock = Boolean(selectedId && unblockResaveIds[selectedId]);
  const receipt = selectedId && receipts[selectedId] ? { contentId: selectedId, result: receipts[selectedId] } : null;
  const retry = selectedId && retries[selectedId] ? { contentId: selectedId, body: retries[selectedId] } : null;
  const foreignOps = useMemo(
    () =>
      Object.keys({ ...receipts, ...retries })
        .filter(id => id && id !== selectedId && (retries[id] || receipts[id]?.status === 'PENDING'))
        .map(contentId => ({
          contentId,
          result: receipts[contentId] ?? null,
          retry: retries[contentId] ? { contentId, body: retries[contentId] } : null
        })),
    [receipts, retries, selectedId]
  );

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      listSequence.current++;
      statsSequence.current++;
      detailSequence.current++;
    };
  }, []);

  const loadList = useCallback(async () => {
    const sequence = ++listSequence.current;
    setListLoading(true);
    setListError(null);
    setPage(null);
    try {
      const result = await messageApi.list(currentQuery.current);
      if (sequence === listSequence.current) setPage(result);
    } catch (error) {
      if (sequence === listSequence.current) setListError(normalizeError(error));
    } finally {
      if (sequence === listSequence.current) setListLoading(false);
    }
  }, []);
  const loadStats = useCallback(async () => {
    const sequence = ++statsSequence.current;
    setStatsLoading(true);
    setStatsError(null);
    setStats(null);
    try {
      const result = await messageApi.stats();
      if (sequence !== statsSequence.current) return;
      if (!isMessageStats(result)) throw new MessageApiError('快讯接口数据异常');
      setStats(result);
    } catch (error) {
      if (sequence === statsSequence.current) setStatsError(normalizeError(error));
    } finally {
      if (sequence === statsSequence.current) setStatsLoading(false);
    }
  }, []);
  useEffect(() => {
    void loadList();
  }, [loadList, query]);
  useEffect(() => {
    void loadStats();
  }, [loadStats]);
  const refreshList = useCallback(() => {
    void loadList();
    void loadStats();
  }, [loadList, loadStats]);
  const submitQuery = useCallback(
    (next: MessageQuery) => {
      setQuery(next);
      void loadStats();
    },
    [loadStats]
  );

  const loadDetail = useCallback(async (id: string, preserve = false) => {
    selected.current = id;
    setSelectedId(id);
    const sequence = ++detailSequence.current;
    setDetailLoading(true);
    setDetailError(null);
    if (!preserve) {
      detailInitialized.current = false;
      setDetail(null);
      setFields(emptyFields);
      setReviewBaselineId(null);
      setDirty(false);
      setNotice('');
      setActionError(null);
      setSeedSource('empty');
    }
    try {
      const result = await messageApi.detail(id);
      if (sequence !== detailSequence.current) return result;
      setDetail(result);
      if (!detailInitialized.current) {
        const seeded = initialFields(result);
        setFields(seeded.fields);
        setSeedSource(seeded.source);
        setReviewBaselineId(result.review?.id ?? null);
        setCommittedReasons(current => ({ ...current, [id]: seeded.fields.reason }));
        detailInitialized.current = true;
      }
      return result;
    } catch (error) {
      if (sequence === detailSequence.current) setDetailError(normalizeError(error));
      return null;
    } finally {
      if (sequence === detailSequence.current) setDetailLoading(false);
    }
  }, []);

  function changeField<K extends keyof ReviewFields>(name: K, value: ReviewFields[K]) {
    setFields(current => ({ ...current, [name]: value }));
    if (name !== 'reason') setDirty(true);
  }

  function markBusy(id: string, busy: boolean) {
    setWriteBusyIds(current => {
      if (busy) return { ...current, [id]: true };
      const next = { ...current };
      delete next[id];
      return next;
    });
  }
  function markQuerying(id: string, value: boolean) {
    setQueryingIds(current => {
      if (value) return { ...current, [id]: true };
      const next = { ...current };
      delete next[id];
      return next;
    });
  }

  async function save() {
    if (!detail?.capabilities.canWrite || !detail.analysis || writeBusyIds[detail.id]) return;
    if (detail.analysis.articleSchemaVersion !== '2') return;
    if (collectFieldIssues(fields).length || reasonIssue(fields.reason)) return;
    const id = detail.id;
    markBusy(id, true);
    setActionError(null);
    setNotice('');
    try {
      const saved = await messageApi.save(id, {
        ...fields,
        articleSchemaVersion: '2',
        expectedSourceVersion: detail.sourceVersion,
        expectedEditorialVersion: detail.editorialVersion,
        analysisId: detail.analysis.id
      });
      if (mounted.current && selected.current === id) {
        setReviewBaselineId(saved.reviewId);
        setLastSavedIds(current => ({ ...current, [id]: saved.reviewId }));
        setDirty(false);
        setCommittedReasons(current => ({ ...current, [id]: fields.reason }));
        setUnblockResaveIds(current => {
          const next = { ...current };
          delete next[id];
          return next;
        });
        setConflictIds(current => {
          const next = { ...current };
          delete next[id];
          return next;
        });
        setNotice('审核已保存，尚未提交发布');
        await loadDetail(id, true);
      }
      if (mounted.current) void loadList();
    } catch (error) {
      if (mounted.current && selected.current === id) {
        const normalized = normalizeError(error);
        setActionError(normalized);
        if (normalized.status === 409) {
          setConflictIds(current => ({ ...current, [id]: true }));
        }
      }
    } finally {
      if (mounted.current) markBusy(id, false);
    }
  }

  async function send(captured: { contentId: string; body: CommandRequest }) {
    markBusy(captured.contentId, true);
    setActionError(null);
    setNotice('');
    try {
      const result = await messageApi.command(captured.contentId, captured.body);
      if (!mounted.current) return;
      setReceipts(current => ({ ...current, [captured.contentId]: result }));
      setRetries(current => {
        const next = { ...current };
        delete next[captured.contentId];
        return next;
      });
      setPollTimedOutIds(current => {
        const next = { ...current };
        delete next[captured.contentId];
        return next;
      });
      pollStartedAt.current[captured.contentId] = Date.now();
      let nextDetail: MessageDetail | null = null;
      if (selected.current === captured.contentId) {
        nextDetail = await loadDetail(captured.contentId, true);
        setCommittedReasons(current => ({ ...current, [captured.contentId]: captured.body.reason }));
      }
      if (result.status === 'COMPLETED') {
        setNotice(commandEffectNotice(captured.body.type, nextDetail));
        if (captured.body.type === 'UNBLOCK') {
          setUnblockResaveIds(current => ({ ...current, [captured.contentId]: true }));
        }
      }
      if (result.status === 'REJECTED' && /MISMATCH|STALE|CONFLICT|VERSION/i.test(result.resultCode || '')) {
        setConflictIds(current => ({ ...current, [captured.contentId]: true }));
      }
      if (mounted.current) void loadList();
    } catch (error) {
      if (!mounted.current) return;
      const normalized = normalizeError(error);
      if (selected.current === captured.contentId) setActionError(normalized);
      if (normalized.status === 409) {
        setConflictIds(current => ({ ...current, [captured.contentId]: true }));
      }
      // A lost response may already have committed. Retain the exact immutable request.
      if (!normalized.status || normalized.status >= 500) {
        setRetries(current => ({ ...current, [captured.contentId]: captured.body }));
      } else {
        setRetries(current => {
          const next = { ...current };
          delete next[captured.contentId];
          return next;
        });
      }
    } finally {
      if (mounted.current) markBusy(captured.contentId, false);
    }
  }

  function command(type: CommandType) {
    if (!detail?.capabilities.canWrite || writeBusyIds[detail.id] || retries[detail.id]) return;
    if (type === 'PUBLISH' && (dirty || reviewMismatch || !compatibleReview(detail))) return;
    if (reasonIssue(fields.reason)) return;
    try {
      void send({
        contentId: detail.id,
        body: {
          commandId: commandUuid(),
          type,
          expectedSourceVersion: detail.sourceVersion,
          expectedEditorialVersion: detail.editorialVersion,
          reason: fields.reason
        }
      });
    } catch (error) {
      setActionError(normalizeError(error));
    }
  }

  async function queryCommand(contentId: string, commandId: string, refreshDetail = true) {
    if (pollInFlight.current[contentId]) return;
    pollInFlight.current[contentId] = true;
    markQuerying(contentId, true);
    try {
      const result = await messageApi.commandStatus(commandId);
      if (!mounted.current) return;
      if (!result?.commandId || (result.status !== 'PENDING' && result.status !== 'COMPLETED' && result.status !== 'REJECTED')) {
        return;
      }
      setReceipts(current => ({ ...current, [contentId]: result }));
      let nextDetail: MessageDetail | null = null;
      if (refreshDetail && selected.current === contentId) {
        nextDetail = await loadDetail(contentId, true);
      }
      if (result.status === 'COMPLETED') {
        const type =
          nextDetail?.commands.find(item => item.commandId === commandId)?.type ||
          detail?.commands.find(item => item.commandId === commandId)?.type;
        if (type) {
          setNotice(commandEffectNotice(type, nextDetail));
          if (type === 'UNBLOCK') setUnblockResaveIds(current => ({ ...current, [contentId]: true }));
        } else if (!nextDetail) {
          setNotice('操作已完成，页面状态待刷新');
        }
        if (mounted.current) void loadList();
      }
      if (result.status === 'REJECTED') {
        if (selected.current === contentId) {
          setActionError(new MessageApiError(result.resultCode || '命令被拒绝'));
        }
        if (/MISMATCH|STALE|CONFLICT|VERSION/i.test(result.resultCode || '')) {
          setConflictIds(current => ({ ...current, [contentId]: true }));
        }
        if (mounted.current) void loadList();
      }
    } catch (error) {
      if (mounted.current && selected.current === contentId) setActionError(normalizeError(error));
    } finally {
      pollInFlight.current[contentId] = false;
      if (mounted.current) markQuerying(contentId, false);
    }
  }

  async function refreshCommand() {
    const current = selectedId && receipts[selectedId];
    if (!current || writeBusyIds[selectedId]) return;
    await queryCommand(selectedId, current.commandId, true);
  }

  const pendingCommandId =
    (selectedId && receipts[selectedId]?.status === 'PENDING' && receipts[selectedId].commandId) ||
    (detail?.id === selectedId ? detail.commands.find(item => item.status === 'PENDING')?.commandId : undefined) ||
    '';

  useEffect(() => {
    if (!drawerOpen || !selectedId || !pendingCommandId) {
      if (!drawerOpen && selectedId) delete pollStartedAt.current[selectedId];
      return;
    }
    if (!pollStartedAt.current[selectedId]) pollStartedAt.current[selectedId] = Date.now();
    let cancelled = false;
    let timer: number | undefined;
    const tick = (refreshDetail: boolean) => {
      if (cancelled) return;
      if (Date.now() - pollStartedAt.current[selectedId] >= 60000) {
        setPollTimedOutIds(current => ({ ...current, [selectedId]: true }));
        return;
      }
      void queryCommand(selectedId, pendingCommandId, refreshDetail).then(() => {
        if (cancelled) return;
        timer = window.setTimeout(() => tick(false), 2000);
      });
    };
    const submittedJustNow = Date.now() - pollStartedAt.current[selectedId] < 800;
    if (submittedJustNow) {
      timer = window.setTimeout(() => tick(true), 2000);
    } else {
      tick(true);
    }
    return () => {
      cancelled = true;
      if (timer) window.clearTimeout(timer);
    };
    // Auto-query is bounded to one pending command per open drawer session.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [drawerOpen, selectedId, pendingCommandId]);

  function retryCommand(contentId = selectedId) {
    const body = retries[contentId];
    if (!body || writeBusyIds[contentId]) return;
    void send({ contentId, body });
  }

  function adoptLatestReview() {
    if (!detail?.review) return;
    const review = detail.review;
    setFields(current => ({
      ...structuredFrom(review),
      title: review.title,
      summary: review.summary,
      interpretation: review.interpretation,
      importance: review.importance,
      featured: review.featured,
      reason: current.reason
    }));
    setReviewBaselineId(review.id);
    setDirty(false);
    setSeedSource('review');
    setConflictIds(current => {
      const next = { ...current };
      delete next[detail.id];
      return next;
    });
  }

  function keepCurrentEdits() {
    if (!selectedId) return;
    setDirty(true);
    setConflictIds(current => {
      const next = { ...current };
      delete next[selectedId];
      return next;
    });
  }

  return {
    query,
    setQuery,
    submitQuery,
    page,
    listLoading,
    listError,
    loadList,
    refreshList,
    stats,
    statsLoading,
    statsError,
    detail,
    detailLoading,
    detailError,
    selectedId,
    loadDetail,
    fields,
    changeField,
    dirty,
    articleDirty,
    reasonDirty,
    reviewMismatch,
    busy: writeBusy,
    writeBusy,
    querying,
    pollTimedOut,
    conflictPending,
    requireSaveAfterUnblock,
    seedSource,
    lastSavedReviewId: selectedId ? lastSavedIds[selectedId] ?? null : null,
    actionError,
    notice,
    save,
    command,
    receipt,
    retry,
    foreignOps,
    retryCommand,
    refreshCommand,
    refreshDetail: () => selected.current && void loadDetail(selected.current, true),
    drawerOpen,
    setDrawerOpen,
    adoptLatestReview,
    keepCurrentEdits
  };
}
